import {
  SHADES_PER_COLOR,
  TIMELINE_AGENT_PALETTE,
} from 'llm/chat-library/foundries/AgentFoundryTimelinePalette';

import {
  AgentFoundryTimelineResponse,
  TimelineAgentResponse,
  TimelineRange,
  TimelineTurnResponse,
} from './GetAgentFoundryTimeline';

interface RangeConfig {
  label: string;
  /** Distance between ruler ticks, which sit on round local-time boundaries. */
  tickEveryHours: number;
}

export const DEFAULT_TIMELINE_RANGE: TimelineRange = '1d';

export const TIMELINE_RANGES: Record<TimelineRange, RangeConfig> = {
  '4h': {label: 'Past 4 hours', tickEveryHours: 1},
  '1d': {label: 'Past day', tickEveryHours: 3},
  '1w': {label: 'Past week', tickEveryHours: 24},
};

export interface TimelineWindow {
  startMs: number;
  endMs: number;
}

function isoToMs(iso: string): number {
  return new Date(iso).getTime();
}

export function getTimelineWindow(response: AgentFoundryTimelineResponse): TimelineWindow {
  return {startMs: isoToMs(response.windowStart), endMs: isoToMs(response.windowEnd)};
}

/** Fraction of the window (0 at its left edge, 1 at "now") that a moment falls at, unclamped. */
function toFraction(timeWindow: TimelineWindow, timeMs: number): number {
  return (timeMs - timeWindow.startMs) / (timeWindow.endMs - timeWindow.startMs);
}

export interface TimelineTick {
  leftPercent: number;
  label: string;
}

/**
 * Ruler ticks at round local-time boundaries inside the window: every `tickEveryHours` local hours
 * (aligned to local midnight), and for the week view every local midnight. Walks local wall-clock time
 * one hour at a time rather than adding fixed milliseconds so DST shifts do not misalign the ticks.
 */
export function getTimelineTicks(timeWindow: TimelineWindow, range: TimelineRange): Array<TimelineTick> {
  const {tickEveryHours} = TIMELINE_RANGES[range];
  const formatter =
    range === '1w'
      ? new Intl.DateTimeFormat(undefined, {weekday: 'short', month: 'short', day: 'numeric'})
      : new Intl.DateTimeFormat(undefined, {hour: 'numeric', minute: '2-digit'});

  const cursor = new Date(timeWindow.startMs);
  cursor.setMinutes(0, 0, 0);
  const ticks: Array<TimelineTick> = [];
  while (cursor.getTime() <= timeWindow.endMs) {
    const isOnBoundary = cursor.getHours() % tickEveryHours === 0;
    if (isOnBoundary && cursor.getTime() >= timeWindow.startMs) {
      ticks.push({
        leftPercent: toFraction(timeWindow, cursor.getTime()) * 100,
        label: formatter.format(cursor),
      });
    }
    cursor.setHours(cursor.getHours() + 1);
  }
  return ticks;
}

export interface TimelineBoxGeometry {
  leftPercent: number;
  widthPercent: number;
}

/**
 * Where a turn's box sits in the window. Only the rendered geometry is clamped to the window; the turn's
 * own times are untouched. A turn with no end time runs to "now" when it is in flight, and is a
 * zero-width sliver (later widened to the minimum box width by CSS) when it never got a response.
 */
export function getTurnBoxGeometry(
  turn: TimelineTurnResponse,
  timeWindow: TimelineWindow
): TimelineBoxGeometry {
  const startMs = isoToMs(turn.startedAt);
  let endMs: number;
  if (turn.endedAt != null) {
    endMs = isoToMs(turn.endedAt);
  } else if (turn.state === 'in_flight') {
    endMs = timeWindow.endMs;
  } else {
    endMs = startMs;
  }

  const clampedStart = Math.min(Math.max(toFraction(timeWindow, startMs), 0), 1);
  const clampedEnd = Math.min(Math.max(toFraction(timeWindow, endMs), clampedStart), 1);
  return {leftPercent: clampedStart * 100, widthPercent: (clampedEnd - clampedStart) * 100};
}

export interface TimelineRow {
  agent: TimelineAgentResponse;
  /** This agent's base color shades, light to dark; see TIMELINE_AGENT_PALETTE. */
  shades: ReadonlyArray<string>;
  turns: Array<TimelineTurnResponse>;
  /** Fill color per chat session id, for the sessions that have a turn in the window. */
  colorByChatSessionId: Map<string, string>;
}

/**
 * One row per roster entry, in roster order, each with its own palette color (by roster index, modulo the
 * palette length) and a shade per chat session.
 *
 * Shade assignment is window-relative: sessions are ordered by the start of their first turn inside the
 * returned window and take `index % SHADES_PER_COLOR`, so switching range can recolor a session. That is
 * accepted for now rather than hashing the session id, which would give stable but arbitrary shades.
 */
export function buildTimelineRows(response: AgentFoundryTimelineResponse): Array<TimelineRow> {
  return response.agents.map((agent, agentIndex) => {
    const {shades} = TIMELINE_AGENT_PALETTE[agentIndex % TIMELINE_AGENT_PALETTE.length];
    const turns = response.turns
      .filter((turn) => turn.agentId === agent.agentId)
      .sort((a, b) => isoToMs(a.startedAt) - isoToMs(b.startedAt));

    const colorByChatSessionId = new Map<string, string>();
    turns.forEach((turn) => {
      if (!colorByChatSessionId.has(turn.chatSessionId)) {
        colorByChatSessionId.set(turn.chatSessionId, shades[colorByChatSessionId.size % SHADES_PER_COLOR]);
      }
    });
    return {agent, shades, turns, colorByChatSessionId};
  });
}

export function formatTurnDuration(turn: TimelineTurnResponse): string {
  if (turn.state === 'in_flight') {
    return 'Running now';
  }
  if (turn.endedAt == null) {
    return 'No response recorded';
  }
  const totalSeconds = Math.max(0, Math.round((isoToMs(turn.endedAt) - isoToMs(turn.startedAt)) / 1000));
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m ${seconds}s`;
}
